import { DeleteTwoTone, EyeOutlined, FileTextOutlined } from '@ant-design/icons';
import recruitApi from 'api/recruitApi';
import { Checkbox, Modal, Select } from 'antd';
import { selectCurrencyFormat } from 'app/commonRedux/appSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { selectShowMoney } from 'features/auth/authSlice';
import { ReasonForm } from 'components/Forms';
import ITVTable from 'components/ITVTable';
import MenuAction from 'components/MenuAction';
import { YYYY_MM_DD } from 'constants/commons';
import { selectEmployee } from 'features/employee/employeeSlice';
import {
  JobStatus,
  isApproveOffer,
  isInterviewer,
} from 'features/recruitment/jobs/components/types';
import {
  deleteRecruitmentCandidate,
  getCandidateManagement,
  getCandidateManagementInterviewer,
  sendOfferApproverStatus,
} from 'features/recruitment/recruitmentAction';
import { actions, selectRecruitmentData } from 'features/recruitment/recruitmentSlice';
import moment from 'moment';
import numeral from 'numeral';
import { useEffect, useState, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Reason } from 'types';
import { getUser } from 'utils/auth';
import { getCandidateSourceLabel, getCandidateSourceTagColors } from '../utils/candidateSource';
import { notificationToast } from 'components/notificationToast';

enum HiringProcessStatusCandidate {
  APPLICATION_RECEIVED = 'Application Received',
  SHORTLISTED = 'Shortlisted',
  IN_PROGRESS = 'In Progress',
  JOB_OFFER = 'Job Offer',
  OFFER_ACCEPTED = 'Offer Accepted',
  OFFER_REJECTED = 'Offer Rejected',
  CLOSED = 'Closed',
}

interface CandidatesListProps {
  setData: (ids: any[]) => void;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  currentPage: number;
  setSizePage: Dispatch<SetStateAction<number>>;
  sizePage: number;
  callBack: () => void;
  status: any;
  filter: any;
}

const CandidatesList = ({
  setData: setSelectedData,
  setCurrentPage,
  currentPage,
  setSizePage,
  sizePage,
  callBack,
  status,
  filter,
}: CandidatesListProps) => {
  const { t } = useTranslation(['recruitment', 'action', 'timesheet']);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const recruitment = useAppSelector(selectRecruitmentData);
  const candidateManagement = recruitment?.candidateManagement?.result;
  const loadingCandidateManagement = recruitment?.loadingCandidateManagement;
  const currencyFormat = useAppSelector(selectCurrencyFormat);
  const isShowMoney = useAppSelector(selectShowMoney);
  const [data, setData] = useState([]);
  const [offerModalCandidate, setOfferModalCandidate] = useState<any>();
  const [offerTemplate, setOfferTemplate] = useState<'employment' | 'internship'>('employment');
  const [generatedOfferUrl, setGeneratedOfferUrl] = useState('');
  const [hasReviewedOffer, setHasReviewedOffer] = useState(false);
  const [isGeneratingOffer, setIsGeneratingOffer] = useState(false);
  const [isSendingOffer, setIsSendingOffer] = useState(false);
  const selectRowKey = useAppSelector(selectEmployee).selectRowKey;
  const { resetcandidateManagement } = actions;
  const statusesToCheck = [
    HiringProcessStatusCandidate.JOB_OFFER,
    HiringProcessStatusCandidate.OFFER_ACCEPTED,
    HiringProcessStatusCandidate.OFFER_REJECTED,
  ];
  const isStatusIncluded = statusesToCheck.some((statusToCheck) => status?.includes(statusToCheck));
  const havePermissionToShowColumn = getUser()?.permissions?.some(
    (i) => i === 'RECRUIT_JOB_OFFER' || i === 'RECRUIT_APPROVE_OFFER',
  );

  const openOfferTemplateModal = (candidate: any) => {
    const defaultTemplate =
      candidate?.offerTemplate === 'internship' ||
      (!candidate?.offerTemplate && /intern/i.test(candidate?.positionName || ''))
        ? 'internship'
        : 'employment';

    setOfferTemplate(defaultTemplate);
    setGeneratedOfferUrl('');
    setHasReviewedOffer(false);
    setOfferModalCandidate(candidate);
  };

  const closeOfferModal = () => {
    if (generatedOfferUrl) URL.revokeObjectURL(generatedOfferUrl);
    setGeneratedOfferUrl('');
    setHasReviewedOffer(false);
    setOfferModalCandidate(undefined);
  };

  const handleGenerateOffer = async () => {
    if (!offerModalCandidate?.jobCandidateId) return;

    setIsGeneratingOffer(true);
    try {
      const response = await recruitApi.generateOfferLetter({
        candidateId: offerModalCandidate.prevId || offerModalCandidate.candidateId,
        jobCandidateId: offerModalCandidate.jobCandidateId,
        positionName: offerModalCandidate.positionName || '',
        templateName: offerTemplate,
      });
      const dataOffer = String(response?.data?.dataOffer || '').replace(/^"|"$/g, '');
      if (!dataOffer) throw new Error(t('recruitment:error_generate_offer_letter'));

      const byteCharacters = atob(dataOffer);
      const byteNumbers = Array.from(byteCharacters, (character) => character.charCodeAt(0));
      const nextUrl = URL.createObjectURL(
        new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' }),
      );
      if (generatedOfferUrl) URL.revokeObjectURL(generatedOfferUrl);
      setGeneratedOfferUrl(nextUrl);
      setHasReviewedOffer(false);

      notificationToast(
        'success',
        response?.data?.message || t('recruitment:offer_letter_generated'),
        5,
      );
    } catch (error: any) {
      notificationToast('error', error?.message || t('recruitment:error_generate_offer_letter'), 5);
    } finally {
      setIsGeneratingOffer(false);
    }
  };

  const handleSendOffer = async () => {
    if (!offerModalCandidate?.jobCandidateId || !generatedOfferUrl || !hasReviewedOffer) return;

    setIsSendingOffer(true);
    try {
      const response = await recruitApi.sendJobOfferWithPdf({
        jobCandidateIds: [offerModalCandidate.jobCandidateId],
        status: 'approved',
        offerTemplate,
        reviewConfirmed: true,
      });
      notificationToast(
        'success',
        response?.data?.message || t('recruitment:offer_letter_sent'),
        5,
      );
      closeOfferModal();

      if (isInterviewer()) {
        dispatch(getCandidateManagementInterviewer({ status, ...filter }));
      } else {
        dispatch(getCandidateManagement({ status, ...filter }));
      }
      callBack?.();
    } catch (error: any) {
      notificationToast('error', error?.message || t('recruitment:error_send_offer_letter'), 5);
    } finally {
      setIsSendingOffer(false);
    }
  };

  useEffect(() => {
    dispatch(resetcandidateManagement({}));
    setSizePage(10);
    setCurrentPage(1);
  }, [status]);

  useEffect(() => {
    setSelectedData(selectRowKey);
  }, [selectRowKey, candidateManagement]);

  useEffect(() => {
    const newData = candidateManagement?.map((item, index) => ({
      ...item,
      prevId: (item as any)?.candidateId ?? item?.id,
      id: item?.jobCandidateId ?? (item as any)?.candidateId ?? item?.id,
      specialId: item?.id + '-' + item?.jobCandidateId,
    }));
    setData(newData);
  }, [recruitment]);

  const getCandidateDetailPath = (candidate: any) => {
    const candidateId = candidate?.prevId ?? candidate?.candidateId;
    const jobCandidateId = candidate?.jobCandidateId ?? 'null';
    return candidateId
      ? `/recruitment/candidates/detail/${candidateId}/${jobCandidateId}`
      : undefined;
  };

  const menu = [
    {
      name: t('timesheet:View_Detail'),
      icon: <EyeOutlined style={{ color: '#1890FF' }} />,
      handleClick: (value: any) => {
        const detailPath = getCandidateDetailPath(value);
        if (detailPath) history.push(detailPath);
      },
    },
    {
      name: t('recruitment:view_offer'),
      icon: <FileTextOutlined style={{ color: '#1890FF' }} />,
      handleClick: (value: any) => {
        try {
          const dataOffer =
            typeof value.dataOffer === 'string'
              ? value.dataOffer.replace(/^"|"$/g, '')
              : value.dataOffer;

          if (typeof dataOffer === 'string' && dataOffer.startsWith('JVBERi')) {
            const byteCharacters = atob(dataOffer);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const fileUrl = URL.createObjectURL(blob);
            window.open(fileUrl, '_blank');
          } else {
            window.open(dataOffer, '_blank');
          }
        } catch (error) {
          console.error('Error viewing offer:', error);
          Modal.error({
            title: t('recruitment:error'),
            content: t('recruitment:error_view_offer_letter'),
          });
        }
      },
      isShow: (value: any) => {
        const dataOffer = value?.dataOffer;
        return (
          dataOffer != null && // Kiểm tra không phải null hoặc undefined
          dataOffer !== '' && // Kiểm tra không phải chuỗi rỗng
          dataOffer !== 'null' && // Kiểm tra không phải chuỗi "null"
          dataOffer !== 'undefined' && // Kiểm tra không phải chuỗi "undefined"
          (typeof dataOffer === 'string' ? dataOffer.trim() !== '' : true) // Kiểm tra chuỗi không chỉ chứa khoảng trắng
        );
      },
    },
    {
      name: t('recruitment:generate_offer_letter'),
      icon: <FileTextOutlined style={{ color: '#1890FF' }} />,
      isShow: (value: any) => value?.offerStatus === 'Approved',
      handleClick: openOfferTemplateModal,
    },
    ...(isApproveOffer()
      ? [
          {
            name: t('timesheet:approve'),
            handleClick: (value: any) => {
              let reason = '';
              Modal.confirm({
                title: <div style={{ fontWeight: 'bold' }}>{t('recruitment:reason')}</div>,
                content: (
                  <>
                    <ReasonForm
                      onSubmit={(payload: Reason) => {
                        if (!payload.reason) return;
                        reason = payload.reason;
                      }}
                    />
                  </>
                ),
                okText: t('action:submit'),
                cancelText: t('action:cancel'),
                onOk: () => {
                  dispatch(
                    sendOfferApproverStatus({
                      id: value?.jobCandidateId,
                      body: {
                        offerStatus: 'Approved',
                        reason,
                      },
                      callBack: () => {
                        callBack && callBack();
                      },
                    }),
                  );
                },
                onCancel: () => {},
              });
            },
          },
          {
            name: t('timesheet:Reject'),
            handleClick: (value: any) => {
              let reason = '';
              Modal.confirm({
                title: <div style={{ fontWeight: 'bold' }}>{t('recruitment:reason')}</div>,
                content: (
                  <>
                    <ReasonForm
                      onSubmit={(payload: Reason) => {
                        if (!payload.reason) return;
                        reason = payload.reason;
                      }}
                    />
                  </>
                ),
                okText: t('action:submit'),
                cancelText: t('action:cancel'),
                onOk: () => {
                  dispatch(
                    sendOfferApproverStatus({
                      id: value?.jobCandidateId,
                      body: {
                        offerStatus: 'Rejected',
                        reason,
                      },
                      callBack: () => {
                        callBack && callBack();
                      },
                    }),
                  );
                },
                onCancel: () => {},
              });
            },
          },
        ]
      : []),
    ...((isApproveOffer() || getUser()?.permissions?.includes('RECRUIT_JOB_CREATED')) &&
    !isInterviewer()
      ? [
          {
            name: t('action:delete'),
            type: 'delete',
            icon: <DeleteTwoTone twoToneColor="#B7B7B7" />,
            handleClick: async (value: any) => {
              await dispatch(
                deleteRecruitmentCandidate({
                  id: value?.prevId,
                  callBack: () => {
                    callBack && callBack();
                  },
                }),
              );
            },
          },
        ]
      : []),
  ];
  const formatInterviewDateTime = (dateTimeString: string) => {
    const [date, timeRange] = dateTimeString.split(' ');
    const [startTime, endTime] = timeRange.split('-');

    const formattedDate = moment(date).format('YYYY-MM-DD');
    const formattedStartTime = moment(startTime, 'HH:mm:ss').format('h:mmA');
    const formattedEndTime = moment(endTime, 'HH:mm:ss').format('h:mmA');

    return (
      <div>
        {formattedDate.toLowerCase()}
        <br />
        {formattedStartTime.toLowerCase()}-{formattedEndTime.toLowerCase()}
      </div>
    );
  };

  const renderCandidateSource = (source?: string) => {
    const label = getCandidateSourceLabel(source);
    const colors = getCandidateSourceTagColors(source);

    return (
      <span
        style={{
          display: 'inline-block',
          maxWidth: '100%',
          padding: '2px 8px',
          overflow: 'hidden',
          color: colors.text,
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          background: colors.background,
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
        }}
        title={label}
      >
        {label}
      </span>
    );
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 40,
      minWidth: 40,
      align: 'center',
      render: (_: any, __: any, index: any) => {
        const trueIndex = index + sizePage * (currentPage - 1);
        return <div>{trueIndex + 1}</div>;
      },
    },
    {
      title: t('recruitment:candidate_name'),
      dataIndex: 'candidateName',
      key: 'candidateName',
      width: 150,
      minWidth: 150,
      render: (value: string, record: any) => {
        const detailPath = getCandidateDetailPath(record);
        return detailPath ? (
          <CandidateNameLink to={detailPath} title={value}>
            {value}
          </CandidateNameLink>
        ) : (
          value
        );
      },
    },
    {
      title: t('recruitment:email'),
      dataIndex: 'email',
      key: 'email',
      width: 150,
      minWidth: 150,
    },
    {
      title: t('recruitment:phone_number'),
      dataIndex: 'phone',
      key: 'phone',
      width: 80,
      minWidth: 80,
    },
    {
      title: t('recruitment:applied_position'),
      dataIndex: 'positionName',
      key: 'positionName',
      width: 100,
      minWidth: 100,
    },
    {
      title: t('recruitment:candidate_source'),
      dataIndex: 'source',
      key: 'source',
      width: 110,
      minWidth: 110,
      render: (value: string) => renderCandidateSource(value),
    },
    {
      title: t('recruitment:applied_date'),
      dataIndex: 'appliedDate',
      key: 'appliedDate',
      width: 80,
      minWidth: 80,
      align: 'center',
      render: (value: any) => (value ? moment(value).format(YYYY_MM_DD) : ''),
    },
    ...(!status && (!isInterviewer() || isApproveOffer())
      ? [
          {
            title: t('recruitment:candidate_result'),
            dataIndex: 'result',
            key: 'result',
            width: 80,
            minWidth: 80,
          },
        ]
      : []),
    ...(status === HiringProcessStatusCandidate.IN_PROGRESS
      ? [
          {
            title: t('recruitment:interview_date_time'),
            dataIndex: 'interviewDateAndTime',
            key: 'interviewDateAndTime',
            width: 110,
            minWidth: 110,
            align: 'center',
            render: (value: any) => (value ? formatInterviewDateTime(value) : ''),
          },
          {
            title: t('recruitment:location'),
            dataIndex: 'interviewLocation',
            key: 'interviewLocation',
            width: 80,
            minWidth: 80,
          },
          {
            title: t('recruitment:candidateInterviewStatus'),
            dataIndex: 'candidateInterviewStatus',
            key: 'candidateInterviewStatus',
            width: 80,
            minWidth: 80,
          },
          {
            title: t('recruitment:interviewerStatus'),
            dataIndex: 'interviewerStatus',
            key: 'interviewerStatus',
            width: 80,
            minWidth: 80,
          },
        ]
      : []),
    ...(isStatusIncluded && havePermissionToShowColumn
      ? [
          {
            title: t('recruitment:expected_start_date'),
            dataIndex: 'expectedStartDate',
            key: 'expectedStartDate',
            width: 80,
            minWidth: 80,
            align: 'center',
            render: (value: any) => (value ? moment(value).format(YYYY_MM_DD) : ''),
          },
          {
            title: t('recruitment:salary_offer'),
            dataIndex: 'salaryOffer',
            key: 'salaryOffer',
            width: 80,
            minWidth: 80,
            render: (value: any) => {
              if (value == null || value === '') return '';
              const formatted = numeral(value).format(currencyFormat);
              return isShowMoney ? formatted : '*****';
            },
          },
          {
            title: t('timesheet:status'),
            dataIndex: 'offerStatus',
            key: 'offerStatus',
            width: 80,
            minWidth: 80,
          },
        ]
      : status?.includes(HiringProcessStatusCandidate.IN_PROGRESS) || !status
      ? [
          {
            title: t('recruitment:stage_name'),
            dataIndex: 'stageName',
            key: 'stageName',
            width: 80,
            minWidth: 80,
          },
        ]
      : []),
    ...(status === HiringProcessStatusCandidate.OFFER_REJECTED
      ? [
          {
            title: t('recruitment:rejected_by'),
            dataIndex: 'offerBy',
            key: 'offerBy',
            width: 80,
            minWidth: 80,
          },
        ]
      : []),
    {
      title: t('recruitment:created_date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 80,
      minWidth: 80,
      align: 'center',
      render: (value: any) => (value ? moment(value).format(YYYY_MM_DD) : ''),
    },
    {
      title: t('action:action'),
      key: 'action',
      dataIndex: 'action',
      width: 50,
      minWidth: 50,
      align: 'center',
      fixed: 'right',
      render: (text: any, record: any) => {
        // Filter menu items based on each row data
        let filteredMenu = menu.filter((m: any) => {
          // Nếu item có hàm isShow thì dựa vào kết quả để quyết định hiển thị
          return typeof m.isShow === 'function' ? m.isShow(record) : true;
        });

        // Ẩn nút approve/reject khi offerStatus khác WAITING_FOR_APPROVAL
        if (JobStatus.WAITING_FOR_APPROVAL !== record?.offerStatus) {
          filteredMenu = filteredMenu.filter(
            (m: any) => m.name !== t('timesheet:approve') && m.name !== t('timesheet:Reject'),
          );
        }

        return <MenuAction menu={filteredMenu} data={record} />;
      },
    },
  ];
  return (
    <>
      <ITVTable
        setOffset={setCurrentPage}
        offset={currentPage}
        setLimit={setSizePage}
        loading={loadingCandidateManagement}
        limit={sizePage}
        columns={columns}
        isRowSelect={
          status === HiringProcessStatusCandidate.JOB_OFFER ||
          status === HiringProcessStatusCandidate.IN_PROGRESS
        }
        totalResult={recruitment?.candidateManagement?.totalResult || undefined}
        data={data}
        setSelectedUser={setSelectedData}
        rowKey={['id']}
        width={1500}
      />
      <Modal
        visible={Boolean(offerModalCandidate)}
        width={900}
        title={
          <OfferModalTitle>
            <span>{t('recruitment:generate_and_review_offer')}</span>
            <CustomSendButton
              type="button"
              disabled={
                !generatedOfferUrl || !hasReviewedOffer || isGeneratingOffer || isSendingOffer
              }
              onClick={handleSendOffer}
            >
              {t('recruitment:send_offer_email')}
            </CustomSendButton>
          </OfferModalTitle>
        }
        footer={[
          <button key="cancel" type="button" className="ant-btn" onClick={closeOfferModal}>
            {t('action:cancel')}
          </button>,
          <button
            key="generate"
            type="button"
            className="ant-btn ant-btn-primary"
            disabled={isGeneratingOffer || isSendingOffer}
            onClick={handleGenerateOffer}
          >
            {generatedOfferUrl
              ? t('recruitment:regenerate_offer_letter')
              : t('recruitment:generate_offer_letter')}
          </button>,
        ]}
        confirmLoading={isGeneratingOffer || isSendingOffer}
        maskClosable={!isGeneratingOffer && !isSendingOffer}
        closable={!isGeneratingOffer && !isSendingOffer}
        onCancel={() => !isGeneratingOffer && !isSendingOffer && closeOfferModal()}
      >
        <div style={{ marginBottom: 16 }}>
          <strong>{offerModalCandidate?.candidateName}</strong>
          {offerModalCandidate?.positionName ? ` - ${offerModalCandidate.positionName}` : ''}
        </div>
        <label htmlFor="offer-template-select">
          {t('recruitment:offer_template')} <span style={{ color: '#ff4d4f' }}>*</span>
        </label>
        <Select
          id="offer-template-select"
          value={offerTemplate}
          style={{ width: '100%', marginTop: 6 }}
          onChange={(value: 'employment' | 'internship') => {
            if (generatedOfferUrl) URL.revokeObjectURL(generatedOfferUrl);
            setGeneratedOfferUrl('');
            setHasReviewedOffer(false);
            setOfferTemplate(value);
          }}
          disabled={isGeneratingOffer || isSendingOffer}
        >
          <Select.Option value="employment">
            {t('recruitment:employment_offer_letter')}
          </Select.Option>
          <Select.Option value="internship">
            {t('recruitment:internship_offer_letter')}
          </Select.Option>
        </Select>
        {generatedOfferUrl && (
          <OfferReviewSection>
            <iframe title="Offer letter preview" src={generatedOfferUrl} />
            <Checkbox
              checked={hasReviewedOffer}
              disabled={isSendingOffer}
              onChange={(event) => setHasReviewedOffer(event.target.checked)}
            >
              {t('recruitment:offer_review_confirmation')}
            </Checkbox>
          </OfferReviewSection>
        )}
      </Modal>
    </>
  );
};

const CandidateNameLink = styled(Link)`
  color: inherit;

  &:hover,
  &:focus {
    color: inherit;
    text-decoration: underline;
  }
`;

const OfferModalTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-right: 32px;
`;

const CustomSendButton = styled.button`
  padding: 7px 16px;
  color: #fff;
  background: #f15a29;
  border: 0;
  border-radius: 4px;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const OfferReviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;

  iframe {
    width: 100%;
    height: 520px;
    border: 1px solid #d9d9d9;
  }
`;

export default CandidatesList;
